import { type ReactNode } from 'react';

/**
 * A facet of a task, drawn only when the task holds one or the reader asked for it.
 *
 * Labels, Colour and Goals each rendered a heading, a paragraph of explanation and an empty
 * control whether or not the task used them — 543px of a 384px panel, over a third of it, on an
 * account that had never made a label or a goal. The Colour paragraph described inheritance from
 * a project and a goal the reader did not have. Description, the one field they would type in,
 * started below all of it.
 *
 * Empty means empty: nothing set *and* nothing to inherit from. A section with a value shows it as
 * it always did, because the complaint was never about the features — it was about paying for the
 * ones you declined.
 *
 * **Since Phase 102 this component no longer draws its own offer.** It used to collapse to a
 * one-line "add a colour" link and hold the `open` flag for it, which is why it needed `addLabel`,
 * `isEmpty` and `available`. Seven such one-line offers stacked in the panel were what four
 * persona rounds measured as its weight, so they now live together inside *More* and `TaskDetail`
 * decides, for every section alike, whether to draw it. That left this with one job — the heading
 * and the panel around it — and keeping the old props would have meant two components holding an
 * opinion about the same question.
 *
 * The `available` rule those props carried is not lost; it moved to the offer list in
 * `TaskDetail`, which is where it belongs now: an offer to "add labels" on an account with no
 * labels opens a picker with nothing in it, and the *filters* on the Tasks screen already follow
 * that rule, so the panel offering them was the two surfaces disagreeing about what this person
 * has.
 */
export function FacetSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="panel">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
